import { formatARS, PAYMENT_METHOD_LABELS, type SaleDto } from '@gl/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Spinner } from '@/components/FullScreenLoader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { errorMessage } from '@/lib/api';
import { useCurrentLocal } from '@/lib/local-context';
import { formatBusinessDate, formatTime } from './format';
import { caja, useInvalidateCaja } from './queries';

export function SaleStatusBadge({ sale }: { sale: SaleDto }) {
  if (sale.status === 'VOIDED') return <Badge variant="destructive">Anulada</Badge>;
  if (sale.cashCloseId) return <Badge variant="secondary">Cerrada</Badge>;
  return null;
}

export function SaleLines({ sale }: { sale: SaleDto }) {
  return (
    <ul className="divide-y text-sm">
      {sale.items.map((i) => (
        <li key={i.id} className="flex justify-between gap-3 py-2">
          <span className="min-w-0">
            <span className="font-medium">{i.quantity}×</span> {i.nameSnapshot}
            {i.variantNameSnapshot && <span className="text-muted-foreground"> · {i.variantNameSnapshot}</span>}
          </span>
          <span className="shrink-0 tabular-nums">{formatARS(i.totalCents)}</span>
        </li>
      ))}
      {sale.discountCents > 0 && (
        <li className="flex justify-between py-2 text-muted-foreground">
          <span>Descuento</span>
          <span className="tabular-nums">−{formatARS(sale.discountCents)}</span>
        </li>
      )}
      <li className="flex justify-between py-2 font-semibold">
        <span>Total</span>
        <span className="tabular-nums">{formatARS(sale.totalCents)}</span>
      </li>
    </ul>
  );
}

/** Detalle de una venta con la opción de anularla (si el usuario puede). */
export function SaleDetailDialog({
  sale,
  canVoid,
  onOpenChange,
}: {
  sale: SaleDto | null;
  canVoid: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const local = useCurrentLocal();
  const invalidate = useInvalidateCaja(local.id);
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState('');

  const voidSale = useMutation({
    mutationFn: () => caja.voidSale(local.id, sale!.id, { reason: reason.trim() }),
    onSuccess: (s) => {
      toast.success(`Venta #${s.number} anulada`);
      void invalidate();
      setVoiding(false);
      setReason('');
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const close = (open: boolean) => {
    if (!open) {
      setVoiding(false);
      setReason('');
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={!!sale} onOpenChange={close}>
      <DialogContent>
        {sale && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Venta #{sale.number} <SaleStatusBadge sale={sale} />
              </DialogTitle>
              <DialogDescription>
                {formatBusinessDate(sale.businessDate)} · {formatTime(sale.soldAt, local.timezone)} ·{' '}
                {PAYMENT_METHOD_LABELS[sale.paymentMethod]} · {sale.createdBy.name}
              </DialogDescription>
            </DialogHeader>
            <SaleLines sale={sale} />
            {sale.note && <p className="text-sm text-muted-foreground">Nota: {sale.note}</p>}
            {sale.status === 'VOIDED' && (
              <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                Anulada{sale.voidReason ? `: ${sale.voidReason}` : ''}
              </p>
            )}
            {voiding ? (
              <form
                className="grid gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (reason.trim().length >= 3) voidSale.mutate();
                }}
              >
                <div className="grid gap-1.5">
                  <Label htmlFor="void-reason">¿Por qué la anulás?</Label>
                  <Textarea
                    id="void-reason"
                    rows={2}
                    autoFocus
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Me equivoqué de producto"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setVoiding(false)}>
                    Volver
                  </Button>
                  <Button type="submit" variant="destructive" disabled={reason.trim().length < 3 || voidSale.isPending}>
                    {voidSale.isPending && <Spinner />} Anular venta
                  </Button>
                </DialogFooter>
              </form>
            ) : (
              canVoid &&
              sale.status === 'COMPLETED' &&
              !sale.cashCloseId && (
                <DialogFooter>
                  <Button variant="outline" className="text-destructive" onClick={() => setVoiding(true)}>
                    Anular
                  </Button>
                </DialogFooter>
              )
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
